import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import _ from 'lodash';
// import useExcelData from 'hooks/useExcelData'; // 훅 대신 useState를 직접 사용
import ExcelView from 'components/spreadsheet/ExcelView';
import { toast } from 'react-toastify';

// useExcelData의 기능을 인라인으로 대체
const useInlineExcelData = initialData => {
  const [data, setData] = useState(initialData);
  return { data, setData };
};

const GRNLinesEditModal = ({ show, onHide, selectedItems, onSave }) => {
  const [isSaving, setIsSaving] = useState(false);
  const { data, setData } = useInlineExcelData([]);

  // 셀 값 변경 핸들러: 그리드 변경 사항을 data 상태에 반영
  const handleGridCellsChanged = useCallback(
    changes => {
      if (!changes || changes.length === 0) return;

      setData(prevData => {
        const updatedData = [...prevData];

        changes.forEach(change => {
          const rowIdString = String(change.rowId);
          const columnId = change.columnId;

          if (rowIdString.includes('-header')) return;

          // data의 d.id (숫자일 수 있음)를 문자열로 변환하여 rowIdString과 비교
          const rowIndex = updatedData.findIndex(
            d => String(d.id) === rowIdString,
          );

          if (rowIndex > -1) {
            const newCell = change.newCell;
            let newValue;

            switch (newCell.type) {
              case 'text':
                newValue = newCell.text;
                break;
              case 'number':
                // numberCell은 text 속성을 가지며 숫자로 변환 필요
                newValue = parseFloat(newCell.text);
                if (isNaN(newValue)) newValue = newCell.text;
                break;
              case 'date':
                newValue = newCell.date;
                break;
              case 'dropdown':
                newValue = newCell.selectedValue;
                break;
              default:
                return;
            }

            updatedData[rowIndex] = {
              ...updatedData[rowIndex],
              [columnId]: newValue,
            };
          }
        });
        return updatedData;
      });
    },
    [setData],
  );

  // 데이터 로딩 및 그룹화 로직
  useEffect(() => {
    if (selectedItems?.length) {
      // quantity: 0 이거나 inventory_type: 'reject'인 항목은 편집 대상에서 제외
      const filtered = selectedItems.filter(
        i => i.inventory_type !== 'reject' && i.quantity > 0,
      );

      // grn_line_id 기준으로 그룹화
      const grouped = _.groupBy(filtered, 'grn_line_id');

      // 그룹화된 데이터를 ExcelView에 맞게 병합하여 포맷
      const formatted = Object.values(grouped).map(items => {
        const base = items[0];
        const categories = ['a', 'b', 'c']; // 정의된 카테고리
        const merged = {
          // 그룹의 대표 ID. ReactGrid rowId로 사용됩니다.
          id: base.grn_line_id,
          product_sku: base.product_sku,
          product_name: base.product_name,
          vendor_sku: base.vendor_sku,
          grn_line_id: base.grn_line_id, // 그룹화된 ID 저장
        };
        categories.forEach(cat => {
          const found = items.find(i => i.inventory_type === cat);

          const quantity = found ? found.quantity : 0;
          const shelfCode = found?.shelf_code
            ? String(found.shelf_code).trim()
            : '';
          const itemId = found ? found.id : null;

          merged[`quantity_${cat}`] = quantity;
          merged[`shelf_code_${cat}`] = shelfCode;
          merged[`id_${cat}`] = itemId; // 저장 시 원본 item.id를 추적
        });
        return merged;
      });

      setData(formatted);
    } else {
      setData([]);
    }
  }, [selectedItems, setData]);

  // 컬럼 구조 정의
  const columns = [
    { key: 'product_sku', header: 'Product SKU', disabled: true },
    { key: 'product_name', header: 'Product Name', disabled: true },
    { key: 'vendor_sku', header: 'Vendor SKU', disabled: true },
    ...['a', 'b', 'c'].flatMap(cat => [
      {
        key: `quantity_${cat}`,
        header: 'Qty',
        groupLabel: `Category ${cat.toUpperCase()}`,
        type: 'number',
        disabled: true,
      },
      {
        key: `shelf_code_${cat}`,
        header: 'Shelf Code',
        groupLabel: `Category ${cat.toUpperCase()}`,
        // 수량이 0인 카테고리는 비활성화
        disabled: row => (row[`quantity_${cat}`] || 0) === 0,
      },
    ]),
  ];

  // ExcelView 옵션 (헤더 스타일링 및 병합 전략)
  const excelOptions = {
    enableGroupedHeader: true,
    headerLevels: 2,
    groupLabelKey: 'groupLabel',
    stickyTopRows: 2,
    mergeStrategy: {
      verticalMergeNonGrouped: true,
      horizontalMergeGrouped: true,
    },
    getHeaderStyle: (col, level) => {
      const base = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        textAlign: 'center',
        borderRight: '1px solid #ccc',
        borderBottom: level === 1 ? 'none' : '1px solid #ccc',
        background: level === 1 ? '#e0e0e0' : '#f0f0f0',
      };

      if (level === 1) {
        base.height = 36;
        base.borderTop = '1px solid #333';

        if (col.groupLabel?.startsWith('Category')) {
          const isFirstOfGroup =
            columns.findIndex(c => c.groupLabel === col.groupLabel) ===
            columns.findIndex(c => c.key === col.key);
          if (isFirstOfGroup) {
            base.borderLeft = '2px solid #333';
          }
        }

        if (col.key === columns[columns.length - 1].key) {
          base.borderRight = '2px solid #333';
        }
      } else if (level === 2) {
        base.height = 32;
        base.borderBottom = '2px solid #333';
      }

      return base;
    },
    getColumnWidth: col => {
      if (col.header === 'Qty') return 70;
      if (col.header === 'Shelf Code') return 120;
      if (col.key === 'product_name') return 200;
      return 150;
    },
  };

  const handleSave = useCallback(async () => {
    let isValid = true;
    const categories = ['a', 'b', 'c'];

    // 1. 유효성 검사 (수량 > 0 && Shelf Code 비어있음)
    for (const row of data) {
      for (const cat of categories) {
        const quantity = row[`quantity_${cat}`] || 0;
        const shelfCode = String(row[`shelf_code_${cat}`] || '').trim();

        if (quantity > 0 && shelfCode === '') {
          toast.error(
            `Category ${cat.toUpperCase()}'s [${
              row.product_sku
            }] needed Shelf Code. Please check the row`,
          );
          isValid = false;
          break;
        }
      }
      if (!isValid) break;
    }

    if (!isValid) {
      return;
    }

    // 2. 저장 데이터 생성 (업데이트 대상: quantity > 0 이고 id_{cat}이 null이 아닌 항목)
    setIsSaving(true);
    try {
      const flattened = data.flatMap(row =>
        categories
          // 수량 > 0 이고, 원본 ID (id_{cat})가 있는 항목만 업데이트 대상으로 필터링
          .filter(cat => {
            const quantity = row[`quantity_${cat}`] || 0;
            const id = row[`id_${cat}`];
            return quantity > 0 && id !== null;
          })
          .map(cat => ({
            id: row[`id_${cat}`], // 원본 항목의 ID
            shelf_code: String(row[`shelf_code_${cat}`] || '').trim(),
          })),
      );

      if (flattened.length === 0) {
        toast.info('No Shelf Code to Update. Please check the data.');
        onHide();
        return;
      }

      await onSave(flattened);
      onHide();
    } finally {
      setIsSaving(false);
    }
  }, [data, onSave, onHide]);

  return (
    <Modal show={show} onHide={onHide} fullscreen>
      <Modal.Header closeButton>
        <Modal.Title>Edit GRN Lines</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ display: 'flex', flexDirection: 'column' }}>
        {data.length === 0 ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : (
          <ExcelView
            data={data}
            columns={columns}
            onCellsChanged={handleGridCellsChanged}
            options={excelOptions}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isSaving || data.length === 0}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GRNLinesEditModal;
